import { JSX } from 'preact'
import { StyleImage as StyleImageType } from '@gsc/types'
import { useAppSelector } from '../../store/store'
import { selectWidgetById } from '../../store/widgets'
import { useWidgetContext } from '../useWidgetContext'
import { useStyleData } from '../Style'

type Align = 'left' | 'right' | 'top' | 'bottom'
type Axis = 'X' | 'Y'

function concatStrings(...strs: string[]): string {
  return strs.filter(Boolean).join(' ')
}

const KEBAB_CASE_REGEXP = /[A-Z]/g

function kebabCase(name: string, separator = '-') {
  return name.replace(KEBAB_CASE_REGEXP, (letter, pos) => {
    return (pos ? separator : '') + letter.toLowerCase()
  })
}

function numberToStyle(axis: Axis, align: Align, position: string) {
  const cssOut: Partial<CSSStyleDeclaration> = {}
  cssOut[align] = position
  const match = /^(-?\d+)(.+)?$/.exec(position)
  if (match) {
    const [, stringValue, units = ''] = match
    if (units === '%') {
      const value = parseFloat(stringValue)
      cssOut.transform = `translate${axis}(${align == 'right' || align == 'bottom' ? value : -value}${units})`
    }
  }
  return cssOut
}

function axisToStyle(axis: Axis, positionDesc: string): Partial<CSSStyleDeclaration> {
  let cssOut: Partial<CSSStyleDeclaration> = {}
  const defaultAlign = axis === 'X' ? 'left' : 'top'
  const defaultTransform = `translate${axis}`
  const positionParts = positionDesc.split(/\s+(.*)/, 2)
  if (positionParts.length === 1) {
    if (positionDesc === defaultAlign) {
      cssOut[defaultAlign] = '0'
    } else if (positionDesc === 'right' || positionDesc === 'bottom') {
      cssOut[defaultAlign] = '100%'
      cssOut.transform = `${defaultTransform}(-100%)`
    } else if (positionDesc === 'center') {
      cssOut[defaultAlign] = '50%'
      cssOut.transform = `${defaultTransform}(-50%)`
    } else {
      cssOut = numberToStyle(axis, defaultAlign, positionDesc)
    }
  } else if (positionParts.length === 2) {
    const [align, position] = positionParts
    cssOut = numberToStyle(axis, align as Align, position)
  }
  return cssOut
}

function positionToStyle(positionX: string, positionY: string): Partial<CSSStyleDeclaration> {
  const cssX = axisToStyle('X', positionX)
  const cssY = axisToStyle('Y', positionY)
  return {
    ...cssX,
    ...cssY,
    transform: concatStrings(cssX.transform, cssY.transform),
  }
}

interface StyleImageProps extends StyleImageType {
  previewName?: string
}

export function StyleImage({
  style = {},
  opacity = 1,
  flipX = false,
  flipY = false,
  angle = '0deg',
  transformOrigin,
  zIndex = 0,
  size = '',
  positionX = '0',
  positionY = '0',
  objectFit = 'fill',
  objectPosition = '50% 50%',
  src,
}: StyleImageProps): JSX.Element {
  const imageStyle: Partial<CSSStyleDeclaration> = {
    position: 'absolute',
    objectFit,
    objectPosition,
    ...positionToStyle(positionX, positionY),
    ...(style as CSSStyleDeclaration),
  }

  const [width, height = width] = size.match(/calc\(.+?\)|\S+/gi) || ([''] as RegExpMatchArray)
  if (width && height) {
    imageStyle.width = width
    imageStyle.height = height
  }

  if (zIndex) {
    imageStyle.zIndex = zIndex.toString()
  }

  if (isFinite(opacity) && opacity !== 1) {
    imageStyle.opacity = opacity.toString()
  }

  if (transformOrigin) {
    imageStyle.transformOrigin = transformOrigin
  }

  imageStyle.transform = concatStrings(
    imageStyle.transform,
    flipX ? 'scaleX(-1)' : '',
    flipY ? 'scaleY(-1)' : '',
    parseInt(angle) ? `rotate(${angle})` : '',
  )

  const imageStyleString = Object.keys(imageStyle).reduce((result, key: any) => {
    return result + `${kebabCase(key)}:${imageStyle[key]};`
  }, '')

  return <img src={src} className="image" style={imageStyleString} />
}

export function StyleImages(): JSX.Element {
  const { widgetId } = useWidgetContext()
  const widget = useAppSelector(state => selectWidgetById(state, widgetId))
  const { images = [] } = useStyleData(widget)

  const visibleImages = images.filter(i => !i.hidden)

  if (visibleImages.length === 0) {
    return null
  }

  return (
    <div className="images">
      {visibleImages.map(({ actions: imageActions, ...image }, idx) => (
        <StyleImage key={idx + image.src} {...image} />
      ))}
    </div>
  )
}
